// Hire - in-app QR scanner. Built on `qr-scanner` (Nimiq), the same plugin used
// by AIMS (replacing the flaky html5-qrcode) so the two apps stay consistent.
// Uses the native BarcodeDetector fast-path where available (Android) and an
// inlined WASM worker fallback (iOS). We own the UI to match the redesign.
//
// onResult receives { itemNo, raw } parsed from the scanned tag (tags are full
// URLs like `.../?item=XXX`); the caller decides what to do (navigate to the item).

import React, { useEffect, useRef, useState, useCallback } from 'react';
import QrScanner from 'qr-scanner';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';

// Parse a scanned QR into an item identifier. Tags carry a full URL
// `https://hire.1maintenance.com.au/?item=XXX`. Falls back to treating the raw
// text as the item number so a bare or damaged tag still resolves.
export function parseScan(text) {
  const raw = (text || '').trim();
  try {
    const u = new URL(raw);
    return { itemNo: u.searchParams.get('item') || raw, raw };
  } catch {
    return { itemNo: raw || null, raw };
  }
}

export default function QrScanDialog({ open, onClose, onResult, title = 'Scan item QR' }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const [error, setError] = useState(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [multiCamera, setMultiCamera] = useState(false);

  const stop = useCallback(() => {
    const s = scannerRef.current;
    if (s) {
      try { s.stop(); } catch (_) {}
      try { s.destroy(); } catch (_) {}
      scannerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    stop();
    if (onClose) onClose();
  }, [stop, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    handledRef.current = false;
    setError(null);
    setFlashOn(false);

    QrScanner.hasCamera()
      .then((present) => {
        if (cancelled) return;
        if (!present) { setError('No camera found on this device.'); return; }
        const video = videoRef.current;
        if (!video) return;

        const scanner = new QrScanner(
          video,
          (result) => {
            if (cancelled || handledRef.current) return;
            handledRef.current = true;
            stop();
            const parsed = parseScan(result && result.data ? result.data : result);
            if (onResult) onResult(parsed);
          },
          {
            preferredCamera: 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 8,
            returnDetailedScanResult: true,
          }
        );
        scannerRef.current = scanner;

        scanner
          .start()
          .then(async () => {
            if (cancelled) { stop(); return; }
            try { setHasFlash(await scanner.hasFlash()); } catch (_) { setHasFlash(false); }
            try {
              const cams = await QrScanner.listCameras(true);
              setMultiCamera(Array.isArray(cams) && cams.length > 1);
            } catch (_) { setMultiCamera(false); }
          })
          .catch((err) => {
            if (cancelled) return;
            const name = err && err.name;
            if (name === 'NotAllowedError') setError('Camera permission denied. Allow camera access in your browser settings, then try again.');
            else if (name === 'NotFoundError') setError('No camera found on this device.');
            else setError('Could not start the camera. ' + (err && err.message ? err.message : ''));
          });
      })
      .catch(() => { if (!cancelled) setError('Could not access the camera.'); });

    return () => { cancelled = true; stop(); };
  }, [open, stop, onResult]);

  const toggleFlash = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      await s.toggleFlash();
      setFlashOn(s.isFlashOn());
    } catch (_) { /* flash not controllable - ignore */ }
  };

  const switchCamera = async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      const cams = await QrScanner.listCameras(true);
      if (!cams || cams.length < 2) return;
      const current = s._preferredCamera;
      const next = current === 'user' ? 'environment' : 'user';
      await s.setCamera(next);
      try { setHasFlash(await s.hasFlash()); } catch (_) {}
    } catch (_) { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { background: '#000', borderRadius: 'var(--r, 12px)', overflow: 'hidden', m: { xs: 1, sm: 2 } } }}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1, py: 0.75, background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))' }}>
          <Box sx={{ color: '#fff', fontSize: 14, fontWeight: 600, pl: 1 }}>{title}</Box>
          <Box>
            {multiCamera && (
              <IconButton size="small" onClick={switchCamera} aria-label="Switch camera" sx={{ color: '#fff' }}>
                <CameraswitchIcon fontSize="small" />
              </IconButton>
            )}
            {hasFlash && (
              <IconButton size="small" onClick={toggleFlash} aria-label="Toggle torch" sx={{ color: '#fff' }}>
                {flashOn ? <FlashlightOnIcon fontSize="small" /> : <FlashlightOffIcon fontSize="small" />}
              </IconButton>
            )}
            <IconButton size="small" onClick={handleClose} aria-label="Close scanner" sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {error ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#fff', minHeight: 240, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
            <Box sx={{ fontSize: 14, lineHeight: 1.5 }}>{error}</Box>
            <Button variant="contained" onClick={handleClose} sx={{ alignSelf: 'center', textTransform: 'none' }}>Close</Button>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', background: '#000' }}>
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} muted playsInline />
          </Box>
        )}

        {!error && (
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2, px: 2, py: 1.25,
            textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: 12,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))' }}>
            Point the camera at the item's QR tag
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
